import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, 
  ShoppingBag,
  Clock,
  Heart,
  MessageCircle,
  CheckCircle,
  AlertTriangle,
  Gift,
  Star,
  Timer,
  Bell,
  ThumbsUp
} from 'lucide-react';

export default function BuyerCommitment() {
  const navigate = useNavigate();

  const commitments = [
    {
      title: 'The "Point of No Return" Policy',
      subtitle: 'Unlike large commercial restaurants, a Home Cook prepares ingredients specifically for your portion.',
      icon: Timer,
      color: 'text-blue-500',
      bgColor: 'from-blue-50 to-indigo-50',
      items: [
        {
          title: 'Commitment',
          description: 'Once an order is placed, you have a 10-minute "Grace Window" to cancel.'
        },
        {
          title: 'The Rule',
          description: 'After 10 minutes, the cook has likely started tempering the dal or kneading the dough. If you cancel after this window, you agree to pay 100% of the meal cost. We encourage you to still have the meal delivered or ask the cook to donate it to the community staff.',
          isImportant: true
        }
      ]
    },
    {
      title: 'Respect for the "Last Mile" (Punctuality)',
      subtitle: 'Our delivery partners are often people from within or around your community.',
      icon: Clock,
      color: 'text-orange-500',
      bgColor: 'from-orange-50 to-amber-50',
      items: [
        {
          title: 'Commitment',
          description: 'Please be available to receive the order at the time of delivery.'
        },
        {
          title: 'The Rule',
          description: 'If the delivery partner is at your gate/door, please don\'t make them wait more than 5 minutes. Their time is as valuable as yours, and a delay in one delivery makes the next neighbour\'s food cold.',
          isImportant: true
        }
      ]
    },
    {
      title: 'The "Gratitude & Feedback" Culture',
      subtitle: 'In a hyper-local setup, you aren\'t just a customer; you are a patron of a neighbour\'s skill.',
      icon: Heart,
      color: 'text-pink-500',
      bgColor: 'from-pink-50 to-rose-50',
      items: [
        {
          title: 'Commitment',
          description: 'Greet the delivery partner and the cook (via reviews) with kindness.'
        },
        {
          title: 'The Rule',
          description: 'Constructive criticism is welcome, but keep it private or polite. If you loved the food, a public "Thank You" note on the platform does more for a Home Cook\'s business than any paid advertisement.',
          isImportant: true
        }
      ]
    },
    {
      title: 'Direct Support & Tipping',
      subtitle: 'While service fees are included, 100% of any additional tip goes directly to the person who served you.',
      icon: Gift,
      color: 'text-green-500',
      bgColor: 'from-green-50 to-emerald-50',
      items: [
        {
          title: 'Commitment',
          description: 'If you feel the service was exceptional—especially during lazy holiday morning or late returns to society—consider a small tip.'
        },
        {
          title: 'The Rule',
          description: 'Tipping is never mandatory, but it is a powerful way to show community gratitude for the extra effort taken to bring you a hot, home-cooked meal.',
          isImportant: false
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button
              variant="ghost"
              onClick={() => navigate('/promise')}
              className="pl-0"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold">Buyer's Commitment</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
            <ShoppingBag className="w-12 h-12 text-white" />
          </div>
          <h1 className="font-heading text-4xl font-bold text-foreground mb-4">
            Buyer's Commitment
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            As a valued member of the RuchiBox community, your commitment helps 
            build trust and ensures a great experience for everyone.
          </p>
        </div>

        {/* Commitments */}
        <div className="space-y-8">
          {commitments.map((commitment, idx) => (
            <Card key={idx} className="overflow-hidden">
              <div className={`bg-gradient-to-r ${commitment.bgColor} p-6 border-b`}>
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full bg-white shadow flex items-center justify-center ${commitment.color}`}>
                    <commitment.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-foreground">
                      {commitment.title}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {commitment.subtitle}
                    </p>
                  </div>
                </div>
              </div>
              <CardContent className="p-6">
                <ul className="space-y-4">
                  {commitment.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="flex gap-3">
                      {item.isImportant ? (
                        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <h4 className="font-semibold text-foreground">{item.title}</h4>
                        <p className={`text-sm ${item.isImportant ? 'text-foreground bg-amber-50 p-3 rounded-lg mt-1' : 'text-muted-foreground'}`}>
                          {item.description}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Community Note */}
        <Card className="mt-12 border-2 border-blue-200 bg-blue-50">
          <CardContent className="p-8 text-center">
            <Star className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <h3 className="font-heading text-2xl font-bold text-foreground mb-3">
              Be a Community Champion
            </h3>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Your positive interactions strengthen our community. Every kind word, 
              timely pickup, and fair feedback helps our home cooks thrive and 
              motivates them to cook even better meals for you!
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
